import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, setSessionToken } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputStyle = { background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)', fontFamily: 'var(--font-body)' };

const AuthInput = ({ type, value, onChange, placeholder, required, maxLength, minLength }) => (
  <input
    type={type} value={value} onChange={onChange} placeholder={placeholder}
    required={required} maxLength={maxLength} minLength={minLength}
    className="w-full px-4 py-3 rounded-lg text-sm border outline-none transition-all"
    style={inputStyle}
    onFocus={e => { e.target.style.borderColor = 'rgba(201,168,76,0.5)'; }}
    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
  />
);

export const Landing = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '' });
  const [verifyData, setVerifyData] = useState({ email: '', code: '' });
  const [forgotData, setForgotData] = useState({ email: '' });
  const [resetData, setResetData] = useState({ email: '', code: '', new_password: '' });
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error('Google OAuth no configurado. Usa email/contraseña.');
      return;
    }
    const nonce = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
    sessionStorage.setItem('google_nonce', nonce);
    const params = new URLSearchParams({
      response_type: 'id_token',
      client_id: clientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      scope: 'email profile openid',
      nonce,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      // Store session_token for Bearer auth fallback (works cross-origin, survives cookie issues)
      if (response.data.session_token) {
        setSessionToken(response.data.session_token);
      }
      setUser(response.data.user);
      toast.success('¡Bienvenido de nuevo!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      toast.success('¡Registro exitoso! Revisa tu email para el código de verificación.');
      setVerifyData({ email: registerData.email, code: '' });
      setShowRegister(false);
      setShowVerifyEmail(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-email`, verifyData);
      if (response.data.session_token) {
        setSessionToken(response.data.session_token);
      }
      setUser(response.data.user);
      toast.success('¡Email verificado! Bienvenido a E-Home');
      navigate('/select-plan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, forgotData);
      toast.success('Si el email existe, recibirás instrucciones para restablecer tu contraseña.');
      setResetData({ email: forgotData.email, code: '', new_password: '' });
      setShowForgotPassword(false);
      setShowResetPassword(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el código');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, resetData);
      toast.success('¡Contraseña restablecida! Ya puedes iniciar sesión.');
      setShowResetPassword(false);
      setShowLogin(true);
      setLoginData({ email: resetData.email, password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const resetAllViews = () => {
    setShowLogin(false);
    setShowRegister(false);
    setShowVerifyEmail(false);
    setShowForgotPassword(false);
    setShowResetPassword(false);
  };

  const labelStyle = { color: '#7a7870', fontFamily: 'var(--font-body)', letterSpacing: '0.1em' };

  const GoldBtn = ({ onClick, type = 'button', disabled, children }) => (
    <motion.button type={type} onClick={onClick} disabled={disabled}
      whileHover={{ scale: disabled ? 1 : 1.02 }} whileTap={{ scale: disabled ? 1 : 0.98 }}
      className="w-full py-3 rounded-lg text-sm font-semibold tracking-widest transition-opacity disabled:opacity-40"
      style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.35)', fontFamily: 'var(--font-body)' }}
    >
      {children}
    </motion.button>
  );

  const BackBtn = ({ onClick }) => (
    <button type="button" onClick={onClick} className="w-full text-center text-xs mt-2 transition-colors"
      style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}
      onMouseEnter={e => e.target.style.color = '#e8e6e0'} onMouseLeave={e => e.target.style.color = '#7a7870'}>
      ← Volver
    </button>
  );

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#050508' }}>
      {/* Aurora background */}
      <div className="aurora-bg">
        <div className="aurora-blob" style={{ width: '55vw', height: '55vw', background: 'radial-gradient(circle, #c9a84c 0%, transparent 70%)', top: '-15%', left: '-5%' }} />
        <div className="aurora-blob" style={{ width: '45vw', height: '45vw', background: 'radial-gradient(circle, #00d4c8 0%, transparent 70%)', bottom: '-15%', right: '-5%', animationDelay: '-5s' }} />
      </div>

      <div className="relative z-10 grid lg:grid-cols-2 min-h-screen">
        {/* Left — Branding */}
        <div className="hidden lg:flex flex-col justify-center p-16 relative">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
            {/* Logo */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <svg width="48" height="48" viewBox="0 0 240 240">
                  <path d="M120 50 L40 110 L40 200 L90 200 L90 150 L150 150 L150 200 L200 200 L200 110 Z"
                    fill="none" stroke="#c9a84c" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
                  <rect x="60" y="125" width="25" height="25" fill="none" stroke="#c9a84c" strokeWidth="8" />
                  <rect x="155" y="125" width="25" height="25" fill="none" stroke="#c9a84c" strokeWidth="8" />
                  <rect x="100" y="160" width="40" height="40" fill="none" stroke="#c9a84c" strokeWidth="8" />
                </svg>
                <span className="text-4xl font-black" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>E-Home</span>
              </div>
              <p className="text-lg mb-10" style={{ color: '#7a7870', fontFamily: 'var(--font-body)', lineHeight: 1.7 }}>
                La plataforma inmobiliaria profesional<br />para agentes que quieren crecer.
              </p>
            </div>

            {/* Feature list */}
            <div className="space-y-4">
              {[
                { icon: '◈', label: 'Mapas interactivos con búsqueda por zona', color: '#c9a84c' },
                { icon: '◎', label: 'CRM profesional con pipeline de ventas', color: '#00d4c8' },
                { icon: '◉', label: 'IA integrada para análisis y asesoría', color: '#a78bfa' },
                { icon: '◆', label: 'Gestión de equipos y objetivos', color: '#34d399' },
              ].map((f, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
                  className="flex items-center gap-4">
                  <span className="text-lg flex-shrink-0" style={{ color: f.color }}>{f.icon}</span>
                  <span className="text-sm" style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}>{f.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right — Auth */}
        <div className="flex flex-col justify-center p-6 sm:p-10 lg:p-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="w-full max-w-sm mx-auto">

            {/* Mobile logo */}
            <div className="flex items-center gap-2 mb-8 lg:hidden">
              <svg width="28" height="28" viewBox="0 0 240 240">
                <path d="M120 50 L40 110 L40 200 L90 200 L90 150 L150 150 L150 200 L200 200 L200 110 Z"
                  fill="none" stroke="#c9a84c" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-xl font-black" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>E-Home</span>
            </div>

            {/* Card */}
            <div className="rounded-2xl p-7 border" style={{ background: '#0d0d14', borderColor: 'rgba(255,255,255,0.07)' }}>

              <AnimatePresence mode="wait">
                {!showLogin && !showRegister && !showVerifyEmail && !showForgotPassword && !showResetPassword ? (
                  <motion.div key="home" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                    <h2 className="text-2xl font-bold mb-1" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>Accede ahora</h2>
                    <p className="text-xs mb-7" style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}>Tu plataforma inmobiliaria profesional</p>

                    <div className="space-y-3">
                      <GoldBtn onClick={() => setShowLogin(true)}>INICIAR SESIÓN</GoldBtn>
                      <motion.button onClick={() => setShowRegister(true)}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full py-3 rounded-lg text-sm font-semibold tracking-widest transition-opacity"
                        style={{ background: 'rgba(255,255,255,0.04)', color: '#e8e6e0', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'var(--font-body)' }}
                      >
                        REGISTRARSE
                      </motion.button>

                      <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
                        <span className="text-xs" style={{ color: '#7a7870' }}>O CONTINÚA CON</span>
                        <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
                      </div>

                      <motion.button onClick={handleGoogleLogin}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-3 transition-all"
                        style={{ background: 'rgba(255,255,255,0.05)', color: '#e8e6e0', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'var(--font-body)' }}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Google
                      </motion.button>
                    </div>
                  </motion.div>

                ) : showLogin ? (
                  <motion.div key="login" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                    <h2 className="text-xl font-bold mb-6" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>Iniciar Sesión</h2>
                    <form onSubmit={handleEmailLogin} className="space-y-4">
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>EMAIL</label>
                        <AuthInput type="email" value={loginData.email} onChange={e => setLoginData({...loginData, email: e.target.value})} placeholder="tu@email.com" required />
                      </div>
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>CONTRASEÑA</label>
                        <AuthInput type="password" value={loginData.password} onChange={e => setLoginData({...loginData, password: e.target.value})} placeholder="••••••••" required />
                      </div>
                      <GoldBtn type="submit" disabled={loading}>{loading ? 'Entrando...' : 'ENTRAR'}</GoldBtn>
                      <button type="button" onClick={() => { setShowLogin(false); setShowForgotPassword(true); setForgotData({ email: loginData.email }); }}
                        className="w-full text-center text-xs transition-colors" style={{ color: '#7a7870' }}
                        onMouseEnter={e => e.target.style.color = '#c9a84c'} onMouseLeave={e => e.target.style.color = '#7a7870'}>
                        ¿Olvidaste tu contraseña?
                      </button>
                      <BackBtn onClick={resetAllViews} />
                    </form>
                  </motion.div>

                ) : showRegister ? (
                  <motion.div key="register" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                    <h2 className="text-xl font-bold mb-6" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>Crear cuenta</h2>
                    <form onSubmit={handleEmailRegister} className="space-y-4">
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>NOMBRE</label>
                        <AuthInput type="text" value={registerData.name} onChange={e => setRegisterData({...registerData, name: e.target.value})} placeholder="Tu nombre" required />
                      </div>
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>EMAIL</label>
                        <AuthInput type="email" value={registerData.email} onChange={e => setRegisterData({...registerData, email: e.target.value})} placeholder="tu@email.com" required />
                      </div>
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>CONTRASEÑA</label>
                        <AuthInput type="password" value={registerData.password} onChange={e => setRegisterData({...registerData, password: e.target.value})} placeholder="Mínimo 6 caracteres" required minLength={6} />
                      </div>
                      <GoldBtn type="submit" disabled={loading}>{loading ? 'Creando...' : 'CREAR CUENTA'}</GoldBtn>
                      <p className="text-center text-xs" style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}>Recibirás un código de verificación por email</p>
                      <BackBtn onClick={resetAllViews} />
                    </form>
                  </motion.div>

                ) : showVerifyEmail ? (
                  <motion.div key="verify" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                    <h2 className="text-xl font-bold mb-2" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>Verificar Email</h2>
                    <p className="text-xs mb-6" style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}>Código enviado a <span style={{ color: '#c9a84c' }}>{verifyData.email}</span></p>
                    <form onSubmit={handleVerifyEmail} className="space-y-4">
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>CÓDIGO DE 6 DÍGITOS</label>
                        <input type="text" required maxLength={6} value={verifyData.code}
                          onChange={e => setVerifyData({...verifyData, code: e.target.value.replace(/\D/g, '')})}
                          className="w-full px-4 py-4 rounded-lg text-2xl text-center tracking-[0.4em] font-bold border outline-none"
                          style={{ ...inputStyle, fontFamily: 'var(--font-display)' }}
                          onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,0.5)'}
                          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                          placeholder="000000"
                        />
                      </div>
                      <GoldBtn type="submit" disabled={loading || verifyData.code.length !== 6}>{loading ? 'Verificando...' : 'VERIFICAR'}</GoldBtn>
                      <BackBtn onClick={resetAllViews} />
                    </form>
                  </motion.div>

                ) : showForgotPassword ? (
                  <motion.div key="forgot" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                    <h2 className="text-xl font-bold mb-2" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>Recuperar acceso</h2>
                    <p className="text-xs mb-6" style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}>Te enviaremos un código para restablecer tu contraseña</p>
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>EMAIL</label>
                        <AuthInput type="email" value={forgotData.email} onChange={e => setForgotData({...forgotData, email: e.target.value})} placeholder="tu@email.com" required />
                      </div>
                      <GoldBtn type="submit" disabled={loading}>{loading ? 'Enviando...' : 'ENVIAR CÓDIGO'}</GoldBtn>
                      <BackBtn onClick={() => { setShowForgotPassword(false); setShowLogin(true); }} />
                    </form>
                  </motion.div>

                ) : showResetPassword ? (
                  <motion.div key="reset" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                    <h2 className="text-xl font-bold mb-2" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>Nueva contraseña</h2>
                    <p className="text-xs mb-6" style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}>Código recibido en <span style={{ color: '#c9a84c' }}>{resetData.email}</span></p>
                    <form onSubmit={handleResetPassword} className="space-y-4">
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>CÓDIGO</label>
                        <input type="text" required maxLength={6} value={resetData.code}
                          onChange={e => setResetData({...resetData, code: e.target.value.replace(/\D/g, '')})}
                          className="w-full px-4 py-4 rounded-lg text-2xl text-center tracking-[0.4em] font-bold border outline-none"
                          style={{ ...inputStyle, fontFamily: 'var(--font-display)' }}
                          onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,0.5)'}
                          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                          placeholder="000000"
                        />
                      </div>
                      <div>
                        <label className="text-xs block mb-1.5 tracking-widest" style={labelStyle}>NUEVA CONTRASEÑA</label>
                        <AuthInput type="password" value={resetData.new_password} onChange={e => setResetData({...resetData, new_password: e.target.value})} placeholder="Mínimo 6 caracteres" required minLength={6} />
                      </div>
                      <GoldBtn type="submit" disabled={loading || resetData.code.length !== 6}>{loading ? 'Cambiando...' : 'CAMBIAR CONTRASEÑA'}</GoldBtn>
                      <BackBtn onClick={resetAllViews} />
                    </form>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
